import { useSelector } from "react-redux";
import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { pageBuildingHelpers } from "./logic/PageBuildingHelpers";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { trackPromise } from "react-promise-tracker";
import {
  selectUserData,
  selectUserWebsitePermissions,
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../../features/userSlice";
import { WebsitePermissions } from "../../../../data/user/WebsitePermissions";
import { WebsiteWithUuid } from "../../../../data/website/Website";
import store from "../../../../store";
import { pageBuildingEndPointLogic } from "../../../../logic/AssetManagement/PageManagement - Copy/PageBuilding/pageBuildingEndPointLogic";
import PageHeadlineDetails from "../,,/../../../../components/AssetManagement/PageBuilder/PageHeadlineDetails/PageHeadlineDetails";
import SharedStateContext from "../../../../components/SharedStateProvider";
import { selectSelectedPage } from "../../../../features/assetManagementSlice";
import PageBuilder from "../../../../components/AssetManagement/PageBuilder/PageBuilding/PageBuilder";
import { parseHtmlSectionsToComponents } from "../../../../components/AssetManagement/PageBuilder/utils/parseHtmlSectionToComponents";
import { COMPONENT_TYPES } from "../../../../components/AssetManagement/PageBuilder/utils/constants";
import ReactDOMServer from "react-dom/server";
import SimpleBackdrop from "../../../../components/BackDrop/BackDrop";
import { generateFinalContent } from "../../../../components/AssetManagement/PageBuilder/PageBuilderHelpers/helpers";
import { useDispatch } from "react-redux";
import { Box } from "@mui/system";

export default function PageBuildingEditScreen(userToken: any) {
  // Basic state
  const [searchParams] = useSearchParams();
  const [pageContent, setPageContent] = useState<any>();
  const [pageUrlContent, setPageUrlContent] = useState<any>();
  const [, setPreview] = useState<any>(null);
  const [components, setComponents] = useState([]);
  const [currentpageElements, setCurrentPageElements] = useState<any>();
  const [currentpageElementsOriginal, setCurrentPageElementsOriginal] =
    useState();
  const siteUid = useMemo(
    () => searchParams.get("selectedSite"),
    [searchParams]
  ) as any;

  const allWebsiteList = useSelector(selectUserWebsitePermissions);
  let matchedSiteName: string | null = null;

  if (siteUid) {
    const matchedItem = allWebsiteList.find((siteObj: any) => siteObj[siteUid]);

    if (matchedItem) {
      matchedSiteName = matchedItem[siteUid].ui_site_name;
    }
  }
  // UI state
  const [isPublishOpen, setisPublishOpen] = useState<any>(false);
  const [isElementSaved, setIsElementSaved] = useState(false);
  const [isYesBtnClicked, setIsYesBtnClicked] = useState(false);
  const [ids, setIds] = useState<number[]>([]);

  // Page building state
  const [pageBuildingStructure, setPageBuildingStructure] = useState();
  const [pageStructureDesign, setPageStructureDesign] = useState<any>();
  const [pageDictionary, setPageDictionary] = useState<any>();
  const [siteAllAvailableWidgets, setSiteAllAvailableWidgets] = useState([]);
  const [pageUrlComponents, setPageUrlComponents] = useState<any>();
  const [header, setHeader] = useState<any>();
  const [headerMenu, setHeaderMenu] = useState<any>();
  const [footer, setFooter] = useState<any>();
  const [tasks, setTasks] = useState({}) as any;

  // Error handling and loading
  const [retry, setRetry] = useState(false);
  const [pageIsUpdatedCount, setPageIsUpdatedCount] = useState<number>();
  const [previewCurrentChanges, setPreviewCurrentChanges] = useState<any>([]);
  const [previewLink, setPreviewLink] = useState<any>("");
  const [activeInsertPosition, setActiveInsertPosition] = useState<
    "above" | "below" | null
  >(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedId, setSelectedId] = useState("") as any;

  // Refs for avoiding unnecessary re-renders
  const isInitialLoadRef = useRef(true);
  const loadingOperationsRef = useRef(new Set<string>());

  // Unused state variables removed
  const [, setPageDiscardData] = useState([]);
  const [, setSuccessMessage] = useState("");
  const [, setPageStructureStyle] = useState();
  const [, setIsPageStrError] = useState(false);
  const [, setErrorAccToPageStatus] = useState<number>();

  // Hooks
  const selectedPage = useSelector(selectSelectedPage);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { siteName } = useParams();

  const { pageRefreshStatus, setPageRefreshStatus } =
    useContext(SharedStateContext);

  // Selectors
  const userData = useSelector(selectUserData);
  const userWebsitePermissions = userData?.data
    ?.website_permissions as WebsitePermissions;
  const response = userData?.data?.products;

  // Memoized values - prevent unnecessary recalculations
  const oauthTokenHeader = useMemo(
    () => userToken?.userToken,
    [userToken?.userToken]
  );

  const pageUid = useMemo(
    () => searchParams.get("selectedPage"),
    [searchParams]
  ) as any;

  const selectedSite = useMemo(() => {
    if (!siteUid || !userWebsitePermissions) return null;
    return (
      (userWebsitePermissions.find(
        (website) => Object.keys(website)[0] === siteUid
      ) as WebsiteWithUuid) ?? null
    );
  }, [siteUid, userWebsitePermissions]);

  const pageTitle = useMemo(() => {
    return pageContent?.post_title || selectedPage?.post_title;
  }, [pageContent?.post_title, selectedPage?.post_title]);

  const pageName = useMemo(() => {
    return pageContent?.post_name || selectedPage?.post_name;
  }, [pageContent?.post_name, selectedPage?.post_name]);

  const language = useMemo(() => {
    if (!pageDictionary) return null;
    try {
      return JSON.parse(pageDictionary)?.settings?.lang;
    } catch {
      return null;
    }
  }, [pageDictionary]);

  // Memoized loading state
  const isEssentialDataLoading = useMemo(() => {
    return !siteAllAvailableWidgets?.length || !pageDictionary || !pageContent;
  }, [siteAllAvailableWidgets?.length, pageDictionary, pageContent]);

  // Debounced error handler
  const handleError = useCallback((error: any, context?: string) => {
    const message =
      error instanceof Error
        ? error.message
        : error?.toString() || "Unknown error";
    store.dispatch(
      setDisplayErrorSnackBar({
        state: true,
        message: context ? `${context}: ${message}` : message,
        type: "error",
      })
    );
    store.dispatch(setIsLoading(false));
  }, []);

  // API Functions - Separated for better control
  const loadPageUrlContent = useCallback(
    async (previewUrl: string) => {
      if (!previewUrl || loadingOperationsRef.current.has("urlContent")) return;

      loadingOperationsRef.current.add("urlContent");
      try {
        await pageBuildingEndPointLogic.getPageUrlContent(
          previewUrl,
          setPageUrlContent
        );
        setShowSidebar(false);
        setSelectedId(null);
      } catch (e) {
        handleError(e, "Failed to load page URL content");
      } finally {
        loadingOperationsRef.current.delete("urlContent");
      }
    },
    [handleError]
  );

  const loadSiteWidgets = useCallback(async () => {
    if (!siteUid || loadingOperationsRef.current.has("siteWidgets")) return;

    loadingOperationsRef.current.add("siteWidgets");
    try {
      await pageBuildingEndPointLogic.getSiteAllAvailableWidgets(
        siteUid,
        oauthTokenHeader,
        setSiteAllAvailableWidgets,
        setErrorAccToPageStatus,
        setRetry,
        () => {},
        trackPromise,
        setIsLoading
      );
    } catch (e) {
      handleError(e, "Failed to load site widgets");
    } finally {
      loadingOperationsRef.current.delete("siteWidgets");
    }
  }, [siteUid, oauthTokenHeader, handleError]);

  const loadPageContent = useCallback(async () => {
    if (!siteUid || !pageUid || loadingOperationsRef.current.has("pageContent"))
      return;

    loadingOperationsRef.current.add("pageContent");
    try {
      await pageBuildingEndPointLogic.getPageContent(
        siteUid,
        pageUid,
        oauthTokenHeader,
        setPageContent,
        setPageStructureDesign,
        setPageDictionary,
        setIsLoading,
        setErrorAccToPageStatus,
        setRetry,
        () => {},
        navigate,
        location
      );
    } catch (e) {
      handleError(e, "Failed to load page content");
    } finally {
      loadingOperationsRef.current.delete("pageContent");
    }
  }, [siteUid, pageUid, oauthTokenHeader, navigate, location, handleError]);

  const initializePageStructure = useCallback(async () => {
    if (
      !pageContent ||
      !pageUrlContent ||
      !pageDictionary ||
      !siteAllAvailableWidgets?.length ||
      loadingOperationsRef.current.has("pageStructure")
    )
      return;

    loadingOperationsRef.current.add("pageStructure");
    try {
      store.dispatch(setIsLoading(true));

      pageBuildingHelpers.onUpdateCreatePage(
        setCurrentPageElementsOriginal,
        pageDictionary,
        pageContent,
        pageUrlContent,
        setHeader,
        setHeaderMenu,
        setFooter,
        setPageUrlComponents,
        setPageStructureStyle,
        pageStructureDesign,
        setPageBuildingStructure,
        tasks,
        setTasks,
        siteAllAvailableWidgets,
        setCurrentPageElements
      );
    } catch (e) {
      handleError(e, "Failed to initialize page structure");
      setIsPageStrError(true);
    } finally {
      loadingOperationsRef.current.delete("pageStructure");
      store.dispatch(setIsLoading(false));
    }
  }, [
    pageContent,
    pageUrlContent,
    pageDictionary,
    siteAllAvailableWidgets,
    pageStructureDesign,
    tasks,
    handleError,
  ]);

  const handlePreviewChanges = useCallback(async () => {
    if (
      Object.values(previewCurrentChanges)?.length !== 2 ||
      loadingOperationsRef.current.has("previewChanges")
    )
      return;

    loadingOperationsRef.current.add("previewChanges");
    try {
      store.dispatch(setIsLoading(true));
      pageBuildingHelpers.updateWidgetChangedWithPreviewCurrentChanges(
        previewCurrentChanges,
        pageBuildingStructure,
        currentpageElements,
        setCurrentPageElements
      );
    } catch (e) {
      handleError(e, "Cannot parse new widget changes from preview");
    } finally {
      loadingOperationsRef.current.delete("previewChanges");
      store.dispatch(setIsLoading(false));
    }
  }, [
    previewCurrentChanges,
    pageBuildingStructure,
    currentpageElements,
    handleError,
  ]);

  // Initialize data on mount and when dependencies change
  const initializeAllData = useCallback(async () => {
    if (!pageUid) {
      pageBuildingEndPointLogic.navigateTo404(navigate);
      return;
    }

    if (!siteUid) {
      console.error("Selected site is missing");
      return;
    }

    try {
      store.dispatch(setIsLoading(true));

      // Load initial data
      await Promise.all([loadSiteWidgets(), loadPageContent()]);
    } catch (e) {
      handleError(e, "Failed to load initial data");
    } finally {
      store.dispatch(setIsLoading(false));
    }
  }, [
    pageUid,
    siteUid,
    loadSiteWidgets,
    loadPageContent,
    navigate,
    handleError,
  ]);

  // Initial load effect
  useEffect(() => {
    if (isInitialLoadRef.current || retry || pageRefreshStatus) {
      isInitialLoadRef.current = false;
      initializeAllData();
      if (pageRefreshStatus) {
        setPageRefreshStatus(false);
      }
    }
  }, [initializeAllData, retry, pageRefreshStatus, setPageRefreshStatus]);

  // Update preview when pageContent changes and load URL content
  useEffect(() => {
    const newPreview = pageContent?.preview_link;
    if (newPreview) {
      setPreview(newPreview);
      // Always load page URL content when pageContent changes (not just when preview URL changes)
      loadPageUrlContent(newPreview);
    }
  }, [pageContent, loadPageUrlContent]);

  // Initialize page structure when all required data is available
  useEffect(() => {
    initializePageStructure();
  }, [initializePageStructure]);

  // Handle preview changes
  useEffect(() => {
    handlePreviewChanges();
  }, [handlePreviewChanges]);
  // eslint-disable-next-line

  // Parse components when page elements change
  useEffect(() => {
    if (!currentpageElements) return;

    const html = ReactDOMServer.renderToStaticMarkup(currentpageElements);
    const parsedComponents = parseHtmlSectionsToComponents(html);
    const filteredComponents = parsedComponents.filter((component) => {
      if (component.type !== COMPONENT_TYPES.WIDGET) return true;
      return component.props.className?.includes("internal-widget");
    }) as any;

    setComponents(filteredComponents);
  }, [currentpageElements]);

  // Update element saved state
  useEffect(() => {
    if (!pageContent || !currentpageElements) return;

    const status = pageContent.post_status;
    if (status === "draft" || status === "pending") {
      setIsElementSaved(false);
    } else if (status === "publish") {
      setIsElementSaved(false);
      setisPublishOpen(false);
    }
    // eslint-disable-next-line
  }, [pageContent?.post_status, currentpageElements]);

  // Stable callback functions with integrated URL content loading
  const previewCurrentChange = useCallback(
    async (elementIndex: number) => {
      if (loadingOperationsRef.current.has("previewCurrentChange")) return;

      loadingOperationsRef.current.add("previewCurrentChange");
      try {
        store.dispatch(setIsLoading(true));

        const pageContentJson = generateFinalContent(
          components,
          siteAllAvailableWidgets,
          language
        );

        const data = {
          data: {
            post_content: pageContentJson,
          },
        };

        await pageBuildingEndPointLogic.getPagePreviewForCurrentChanges(
          elementIndex,
          siteUid,
          pageUid,
          data,
          setIsLoading,
          trackPromise,
          setPreviewCurrentChanges,
          oauthTokenHeader,
          setPreviewLink
        );
      } catch (e) {
        handleError(e, "Failed to generate preview");
      } finally {
        loadingOperationsRef.current.delete("previewCurrentChange");
        store.dispatch(setIsLoading(false));
      }
    },
    [
      components,
      siteAllAvailableWidgets,
      language,
      siteUid,
      pageUid,
      oauthTokenHeader,
      handleError,
    ]
  );

  const publishPage = useCallback(async () => {
    if (loadingOperationsRef.current.has("publishPage")) return;

    loadingOperationsRef.current.add("publishPage");
    try {
      store.dispatch(setIsLoading(true));

      const pageContentJson = generateFinalContent(
        components,
        siteAllAvailableWidgets,
        language
      );

      const updateObject = {
        data: {
          post_title: pageContent?.post_title,
          post_content: pageContentJson,
          post_status: "publish",
        },
      };

      await pageBuildingEndPointLogic.updatePage(
        updateObject,
        siteUid,
        pageUid,
        dispatch,
        oauthTokenHeader,
        setPageIsUpdatedCount,
        pageIsUpdatedCount,
        setPreview
      );

      if (pageContent?.unpublished_change === true) {
        await pageBuildingEndPointLogic.discardPage(
          siteUid,
          pageUid,
          setIsLoading,
          oauthTokenHeader,
          setPageDiscardData,
          "",
          setPageIsUpdatedCount,
          pageIsUpdatedCount,
          setPreview
        );
      }

      setSuccessMessage("Page Published Successfully.");
      setPageRefreshStatus(true);

      // Reload page content to get the fresh preview link
      await loadPageContent();
    } catch (e) {
      handleError(e, "Failed to publish page");
    } finally {
      loadingOperationsRef.current.delete("publishPage");
      store.dispatch(setIsLoading(false));
    }
  }, [
    components,
    siteAllAvailableWidgets,
    language,
    pageContent,
    siteUid,
    pageUid,
    dispatch,
    oauthTokenHeader,
    pageIsUpdatedCount,
    setPageRefreshStatus,
    loadPageContent,
    handleError,
  ]);

  const addId = useCallback((newId: number) => {
    setIds((prev) => (prev.includes(newId) ? prev : [...prev, newId]));
  }, []);

  const savePage = useCallback(async () => {
    if (loadingOperationsRef.current.has("savePage")) return;

    loadingOperationsRef.current.add("savePage");
    try {
      store.dispatch(setIsLoading(true));
      const pageContentJson = generateFinalContent(
        components,
        siteAllAvailableWidgets,
        language
      );

      const updateObject = {
        data: {
          post_title: pageContent?.post_title,
          post_content: pageContentJson,
          post_status: "pending",
        },
      };

      await pageBuildingEndPointLogic.savePage(
        updateObject,
        siteUid,
        pageUid,
        dispatch,
        oauthTokenHeader,
        setPageIsUpdatedCount,
        pageIsUpdatedCount,
        setPreview
      );

      setIsElementSaved(false);
      if (pageContent?.page_id) {
        addId(pageContent.page_id);
      }

      if (pageContent?.shared_uid !== undefined) {
        setPageRefreshStatus(true);
      }

      // Reload page content to get the fresh preview link
      await loadPageContent();
    } catch (e) {
      handleError(e, "Failed to save page");
    } finally {
      loadingOperationsRef.current.delete("savePage");
      store.dispatch(setIsLoading(false));
    }
  }, [
    components,
    siteAllAvailableWidgets,
    language,
    pageContent,
    siteUid,
    pageUid,
    dispatch,
    oauthTokenHeader,
    pageIsUpdatedCount,
    addId,
    setPageRefreshStatus,
    loadPageContent,
    handleError,
  ]);

  const unpublishPage = useCallback(async () => {
    if (loadingOperationsRef.current.has("unpublishPage")) return;

    loadingOperationsRef.current.add("unpublishPage");
    try {
      store.dispatch(setIsLoading(true));

      const pageContentJson = generateFinalContent(
        components,
        siteAllAvailableWidgets,
        language
      );

      const updateObject = {
        data: {
          post_title: pageContent?.post_title,
          post_content: pageContentJson,
          post_status: "draft",
        },
      };

      await pageBuildingEndPointLogic.unpublishPage(
        updateObject,
        siteUid,
        pageUid,
        dispatch,
        oauthTokenHeader,
        setPageIsUpdatedCount,
        pageIsUpdatedCount,
        setPreview
      );

      setIsElementSaved(false);
      if (pageContent?.page_id) {
        addId(pageContent.page_id);
      }

      if (pageContent?.shared_uid !== undefined) {
        setSuccessMessage("Page unpublished successfully.");
        setPageRefreshStatus(true);
      }

      // Reload page content to get the fresh preview link
      await loadPageContent();
    } catch (e) {
      handleError(e, "Failed to unpublish page");
    } finally {
      loadingOperationsRef.current.delete("unpublishPage");
      store.dispatch(setIsLoading(false));
    }
  }, [
    components,
    siteAllAvailableWidgets,
    language,
    pageContent,
    siteUid,
    pageUid,
    dispatch,
    oauthTokenHeader,
    pageIsUpdatedCount,
    addId,
    setPageRefreshStatus,
    loadPageContent,
    handleError,
  ]);

  const discardPage = useCallback(async () => {
    if (loadingOperationsRef.current.has("discardPage")) return;

    loadingOperationsRef.current.add("discardPage");
    try {
      store.dispatch(setIsLoading(true));
      setPreview(selectedPage?.preview_link);

      await pageBuildingEndPointLogic.discardPage(
        siteUid,
        pageUid,
        dispatch,
        oauthTokenHeader,
        setPageDiscardData,
        "",
        setPageIsUpdatedCount,
        pageIsUpdatedCount,
        setPreview
      );

      setPageRefreshStatus(true);
      setSuccessMessage("Page discarded successfully.");

      // Reload page content to get the fresh preview link
      await loadPageContent();
    } catch (error) {
      handleError(error, "Failed to discard page");
    } finally {
      loadingOperationsRef.current.delete("discardPage");
      store.dispatch(setIsLoading(false));
    }
  }, [
    selectedPage,
    siteUid,
    pageUid,
    dispatch,
    oauthTokenHeader,
    pageIsUpdatedCount,
    setPageRefreshStatus,
    loadPageContent,
    handleError,
  ]);

  // Memoized props objects
  const pageBuildingProps = useMemo(
    () => ({
      pageUrlComponents,
      pageBuildingStructure,
      header,
      footer,
      headerMenu,
      htmlWrapperDiv: "",
      siteAllAvailableWidgets,
      oauthTokenHeader,
      siteUid,
      setisPublishOpen,
      setIsElementSaved,
      response,
      pageContent,
      pageDictionary,
      currentpageElements,
      components,
      setComponents,
      activeInsertPosition,
      setActiveInsertPosition,
      setShowSidebar,
      showSidebar,
      setSelectedId,
      selectedId,
    }),
    [
      pageUrlComponents,
      pageBuildingStructure,
      header,
      footer,
      headerMenu,
      siteAllAvailableWidgets,
      oauthTokenHeader,
      siteUid,
      response,
      pageContent,
      pageDictionary,
      currentpageElements,
      components,
      activeInsertPosition,
      showSidebar,
      selectedId,
    ]
  );

  const PageHeadlineDetailsProps = useMemo(
    () => ({
      oauthTokenHeader,
      siteUid,
      pageUid,
      pageName,
      siteName,
      publishPage,
      savePage,
      selectedSite,
      previewLink,
      pageContent,
      previewCurrentChange,
      discardPage,
      currentpageElements,
      isPublishOpen,
      isElementSaved,
      pageContentPageId: pageContent?.page_id,
      ids,
      setIsYesBtnClicked,
      isYesBtnClicked,
      setPageContent,
      setPageStructureDesign,
      setPageDictionary,
      setPageUrlContent,
      setSiteAllAvailableWidgets,
      siteAllAvailableWidgets,
      setPageRefreshStatus,
      currentpageElementsOriginal,
      setPreview,
      setSuccessMessage,
      response,
      pageTitle,
      unpublishPage,
      components,
      setComponents,
      matchedSiteName,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      oauthTokenHeader,
      siteUid,
      pageUid,
      pageName,
      siteName,
      publishPage,
      savePage,
      selectedSite,
      previewLink,
      pageContent,
      previewCurrentChange,
      discardPage,
      currentpageElements,
      isPublishOpen,
      isElementSaved,
      pageContent?.page_id,
      ids,
      isYesBtnClicked,
      siteAllAvailableWidgets,
      currentpageElementsOriginal,
      response,
      pageTitle,
      unpublishPage,
      components,
    ]
  );

  return (
    <>
      {isEssentialDataLoading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <PageHeadlineDetails {...PageHeadlineDetailsProps} />
          </Box>
          <PageBuilder {...pageBuildingProps} />
        </>
      )}
    </>
  );
}
